// @flow
import React from 'react'
import classNames from 'classnames'
import Loadable from 'react-loadable'
import ScrollLock from 'react-scrolllock'
import isMobile from 'is-mobile'

import DateInputs from './DateInputs'
import DateTime from './DateTime'
import { RadioTile } from '../../../Components'

// core
import moment from '../../Moment'

// types
import type { DefaultFormFieldState, DefaultFormFieldType } from './AbstractFormField'

const LoadableDatePicker = Loadable({
  loader: () => import('react-datetime'),
  loading() { return <span /> },
})

type DateTimeTilesProps = DefaultFormFieldType & {
  domain: {
    _type: string,
    pattern?: string,
    minDate?: string,
    maxDate?: string
  },
  value: string,
  endOfDay?: boolean,
}

type DateTimeTilesState = DefaultFormFieldState & {
  editCompleted: boolean,
  maxDatetime: Object | null,
  minDatetime: Object | null,
  values: Object | string,
  pickerOpened: boolean,
  customDate: boolean,
}

export default class DateTimeTiles extends DateTime<DateTimeTilesProps, DateTimeTilesState> {

  checkDatetime = (datetime: string) => {
    const dateObject = moment(datetime)
    if (!dateObject.isValid() && datetime !== '') {
      this.props.onChange('')
    } else {
      let newState: Object = {}
      newState.value = dateObject
      if (dateObject.isAfter(moment().endOf('day').add(2, 'days'))) {
        newState.customDate = true
      }
      this.setState(newState)
    }
  }

  handleOnChangeDatePicker = (date: string | Object) => {
    date = date.endOf('day')
    if (date.isSame(moment(), 'day')) {
      date.add(-30, 'minutes')
    } else if (!this.props.endOfDay) {
      date = date.startOf('day')
    }
    this.handleOnChange(date)
    this.setState({ pickerOpened: false })
  }

  handleOnBlurDatePicker = (date: string | Object) => {
    date = date.endOf('day')
    if (date.isSame(moment(), 'day')) {
      date.add(-30, 'minutes')
    } else if (!this.props.endOfDay) {
      date = date.startOf('day')
    }
    this.handleOnBlur(date)
    this.setState({ pickerOpened: false })
  }

  handleOnChangeInputs = (date: string) => {
    if (date) {
      let dateMoment = moment(`${date} 00:00`, 'YYYY-MM-DD HH:mm')
      if (dateMoment.isValid()) {
        if (dateMoment.endOf('day') === moment().endOf('day') || this.props.endOfDay) {
          dateMoment = dateMoment.endOf('day').add(-30, 'minutes')
        } else {
          dateMoment = dateMoment.startOf('day')
        }
        this.handleOnChange(dateMoment)
        this.handleOnBlur(dateMoment)
      } else {
        this.handleOnChange(date)
        this.handleOnBlur(date)
      }
    }
  }

  handleTogglePicker = () => {
    this.setState({ pickerOpened: !this.state.pickerOpened })
  }

  render() {
    if (this.props.textMode) {
      return <span>{this.state.value ? this.state.value.format('DD.MM.YYYY HH:mm:ss') : ''}</span>
    }

    const rowClasses = ['row position-relative']
    const datePickerWrapperClasses = ['react-datepicker  datepicker-button']
    if (this.hasErrors() && !this.isPristine()) {
      rowClasses.push('is-invalid')
      datePickerWrapperClasses.push('is-invalid')
    }

    const tiles = ['Dnes', 'Zítra', 'Jindy']
    const selectedDate = moment(this.state.value)
    let customDate = this.state.customDate

    let valueObject = ''
    if (this.props.value) {
      valueObject = moment(this.props.value)
    }

    // $FlowIssue
    return (
      <div>
        <div className={classNames(rowClasses)}>
          {tiles.map((tile, index) => {
            let date = moment().startOf('day').add(index, 'days')
            if (index === 0) {
              date = moment().endOf('day').add(-30, 'minutes')
            }
            let active = false
            if (index < (tiles.length - 1)) {
              if (!customDate) {
                active = selectedDate.isSame(date, 'days')
              }
              if (active && !customDate) {
                customDate = false
              }
            } else {
              active = customDate
            }
            const label = date.format('D.M.')
            return [
              <RadioTile
                checked={active || false}
                enabled
                icon={index === tiles.length - 1 ? 'pfp pfp-calendar' : ''}
                iconLabel={index === tiles.length - 1 ? '' : label}
                id={label}
                key={tile}
                label={tile}
                name={label}
                onChange={() => {
                  if (index === tiles.length - 1) {
                    this.setState({ customDate: !this.state.customDate, pickerOpened: !this.state.customDate })
                  } else {
                    this.setState({ customDate: false })
                    this.handleOnChange(date)
                    this.handleOnBlur(date)
                  }
                }}
                value={date.format('DD.MM.YYYY')}
              />,
              index === tiles.length - 1
                ? [
                  <div className={classNames(datePickerWrapperClasses)} key='datePicker'>
                    <LoadableDatePicker
                      closeOnSelect
                      input={false}
                      isValidDate={this.handleIsValidDate}
                      locale='cs-CZ'
                      onBlur={this.handleOnBlurDatePicker}
                      onChange={this.handleOnChangeDatePicker}
                      open={this.state.pickerOpened}
                      strictParsing
                      timeFormat={false}
                      value={this.state.value}
                      viewDate={valueObject ? valueObject : this.state.maxDate ? this.state.maxDate : false}
                      viewMode='days'
                    />
                  </div>,
                  this.state.pickerOpened && isMobile() && <div
                    className='modal-backdrop fade show'
                    key='backdrop'
                    onClick={this.handleTogglePicker}
                    style={{ zIndex: 5 }}
                  />
                ]
                : null
            ]
          })}
          {!customDate && this.renderError()}
        </div>

        {this.state.pickerOpened &&
        <div
          onClick={this.handleTogglePicker}
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
        />
        }
        {customDate && [// $FlowIssue
          <DateInputs
            {...this.props}
            className='mt-3'
            doNotRenderDatePicker
            hideButton
            key='dateInputs'
            onChange={this.handleOnChangeInputs}
            value={valueObject ? valueObject.format('YYYY-MM-DD') : ''}
          />,
          this.renderError()
        ]}
        {this.state.pickerOpened && isMobile() && <ScrollLock/>}
      </div>
    )
  }
}
